/**
 * ============================================
 * MARKET THRESHOLD BFE - TDTP
 * ============================================
 *
 * Gedeelde valuta-logica voor de free-shipping drempel uit metaobject
 * 'market_shipping_config'. Eén bron van waarheid voor banner, USP-blok en cart,
 * zodat die drie nooit meer uiteen kunnen lopen.
 *
 * Het probleem dat dit oplost
 * ---------------------------
 * De tiers in het metaobject staan in EUR (bijv. €95 voor HK/JP/SG/...). Liquid's
 * money-filter rendert die waarde met het LOKALE valutateken maar zonder omrekening,
 * dus een HK-klant zag "$95" terwijl de echte drempel ~HK$871 is. Alleen de tiers met
 * een eigen valuta (US $75, UK £65, CH CHF 85, CA/AU/NZ) klopten al — die hebben
 * currency_iso_code == marktvaluta en worden als "lokaal" gemarkeerd.
 *
 * Afronding
 * ---------
 * Een omgerekend bedrag is onafgerond lelijk (HK$871,06). We ronden naar BOVEN af naar
 * een net bedrag. Naar boven is bewust: we tonen nooit een lagere drempel dan Shopify
 * daadwerkelijk hanteert. Wie eroverheen gaat krijgt gratis verzending — een meevaller,
 * geen klacht. Andersom zou een klant het beloofde voordeel mislopen.
 *
 * Let op: de echte drempel is de EUR-zone die Shopify live omrekent, dus het lokale
 * bedrag beweegt mee met de wisselkoers. Daarom converteren we in JS i.p.v. een vast
 * lokaal bedrag in het metaobject te zetten (dat zou binnen maanden gaan afwijken).
 *
 * Intelligems shipping-test
 * --------------------------
 * Loopt er een Intelligems shipping-test, dan geldt voor de bezoeker de drempel van z'n
 * testgroep, niet die uit het metaobject. Na 'ig:ready' leest igData.user.getTestGroup()
 * die drempel (freeShippingThreshold, altijd in EUR = shopvaluta). Alleen type 'shipping'
 * wordt overschreven, nooit de cadeaudrempel. Daarna:
 *   - de display-labels (toolbar, USP-blok) krijgen het testbedrag
 *   - event 'bfe:market-threshold:change' laat cart-rewards-bfe.js opnieuw rekenen
 *   - de Pipeline-melding op /cart (data-cart-message) wordt bijgewerkt, want Pipeline
 *     leest data-limit maar één keer bij init
 * Geen test of niet ingedeeld = metaobject-drempel, er verandert niets.
 * Docs: https://docs.intelligems.io/developer-resources/javascript-api/user-object
 *
 * Versie: 1.1 - 06-10-2026 (Intelligems shipping-test override)
 * Versie: 1.0 - 21-07-2026
 * ============================================
 */
(function () {
  'use strict';

  /**
   * Snap een waarde naar een "net" getal: 1, 2 of 5 maal een macht van tien.
   * 8,71 -> 10 | 160 -> 200 | 11 -> 20 | 1.377 -> 2.000
   */
  function niceUnit(value) {
    if (value <= 0) return 1;
    var magnitude = Math.pow(10, Math.floor(Math.log(value) / Math.LN10));
    var ratio = value / magnitude;
    if (ratio <= 1) return magnitude;
    if (ratio <= 2) return 2 * magnitude;
    if (ratio <= 5) return 5 * magnitude;
    return 10 * magnitude;
  }

  /**
   * Afrondingsstap ≈ 1% van het bedrag, gesnapt op een net getal.
   *
   * Bewust géén vaste staptabel per grootteorde: die geeft cliff-randen. Een drempel die
   * net boven een grens uitkomt (MXN 2.042 met stap 500) zou dan 22% te hoog getoond
   * worden. Met een relatieve stap blijft de buffer overal onder ~2%.
   */
  function roundingStep(amount) {
    return niceUnit(amount * 0.01);
  }

  /**
   * Rond een bedrag (in hele eenheden, niet centen) naar boven af op een net getal.
   */
  function roundUp(amount) {
    var step = roundingStep(amount);
    return Math.ceil(amount / step) * step;
  }

  /**
   * Actieve wisselkoers van de markt. 1 als Shopify niets doorgeeft.
   */
  function currencyRate() {
    return window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
      ? Number(window.Shopify.currency.rate)
      : 1;
  }

  /* Drempel uit de Intelligems-testgroep van deze bezoeker (EUR, hele eenheden), of null */
  var igShippingThreshold = null;

  /**
   * Zoek in de actieve Intelligems-experimenten de testgroep met een verzenddrempel.
   * getExperiments() houdt al rekening met audience-targeting: een DE-bezoeker bij een
   * NL-test krijgt hier niets terug.
   */
  function readIgShippingThreshold() {
    var user = window.igData && window.igData.user;
    if (!user || typeof user.getExperiments !== 'function') return null;

    try {
      var experiments = user.getExperiments() || [];
      for (var i = 0; i < experiments.length; i++) {
        var group = user.getTestGroup(experiments[i].id);
        var value = group && Number(group.freeShippingThreshold);
        if (value > 0) return value;
      }
    } catch (e) {
      /* IG-API onverwacht anders: terugvallen op het metaobject */
    }
    return null;
  }

  /**
   * Reken een drempel om naar de actieve markt-valuta en geef centen terug.
   *
   * @param {number}  amount   drempel in hele eenheden (60, 75, 95, ...)
   * @param {boolean} isLocal  true = bedrag staat al in de actieve valuta (US-tier $75)
   * @param {string}  type     'shipping' (default) of 'gift'. Alleen shipping volgt een
   *                           Intelligems-test.
   * @returns {number} bedrag in centen, afgerond naar boven bij omrekening
   */
  function thresholdCents(amount, isLocal, type) {
    if (igShippingThreshold && (type || 'shipping') === 'shipping') {
      /* IG rekent in EUR, dus altijd omrekenen — ook als het metaobject-tier lokaal was */
      amount = igShippingThreshold;
      isLocal = false;
    }
    var value = Number(amount) || 0;
    if (isLocal) {
      /* Al in de juiste valuta en al een net bedrag — niet aanraken. */
      return Math.round(value * 100);
    }
    return Math.round(roundUp(value * currencyRate()) * 100);
  }

  /**
   * Format centen in de actieve markt-valuta.
   * Bewust geen window.themeCurrency.formatMoney — die gebruikt window.theme.moneyFormat
   * (de EUR-format van de shop), waardoor non-EUR markten altijd "€" zouden zien.
   *
   * @param {number}  cents
   * @param {boolean} withDecimals  false = hele bedragen (afgeronde drempels)
   */
  function formatMoney(cents, withDecimals) {
    var currencyCode =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var locale = (document.documentElement.lang || 'en').replace('_', '-');
    var digits = withDecimals ? 2 : 0;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(digits) + ' ' + currencyCode;
    }
  }

  window.BFEMarketThreshold = {
    roundUp: roundUp,
    currencyRate: currencyRate,
    thresholdCents: thresholdCents,
    formatMoney: formatMoney,
    igShippingThreshold: function () { return igShippingThreshold; }
  };

  /**
   * Upgrade alle server-side gerenderde drempel-labels naar het omgerekende bedrag.
   * Idempotent: rekent altijd vanaf de data-attributen, nooit vanaf de bestaande tekst,
   * dus meerdere keren draaien (of meerdere script-tags op de pagina) is veilig.
   */
  function upgradeLabels(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-market-threshold]');
    var rateIsOne = currencyRate() === 1;

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var isLocal = node.getAttribute('data-threshold-is-local') === 'true';
      var isShipping = (node.getAttribute('data-threshold-type') || 'shipping') === 'shipping';
      var override = isShipping ? igShippingThreshold : null;

      /* Liquid-tekst bewaren, zodat we altijd vanaf het origineel rekenen */
      if (!node.hasAttribute('data-threshold-fallback')) {
        node.setAttribute('data-threshold-fallback', node.textContent);
      }
      var fallback = node.getAttribute('data-threshold-fallback');

      if (rateIsOne) {
        /* EUR-markt: de Liquid-output respecteert de locale-notatie ("€60" in NL, "60 €"
           in FR). Bij een IG-drempel alleen het getal vervangen, de notatie blijft. */
        node.textContent = override
          ? fallback.replace(/\d+([.,]\d+)?/, String(override))
          : fallback;
        continue;
      }

      /* Lokale tiers staan al goed in Liquid — laat die met rust, behalve bij een IG-test */
      if (isLocal && !override) {
        node.textContent = fallback;
        continue;
      }

      var amount = Number(node.getAttribute('data-threshold-amount'));
      if (!amount) continue;

      node.textContent = formatMoney(thresholdCents(amount, isLocal, isShipping ? 'shipping' : 'gift'), false);
    }
  }

  /**
   * Pipeline-melding op /cart (snippets/cart-shipping-message.liquid). Pipeline leest
   * data-limit alleen bij init, dus bij een IG-drempel zetten we tekst, voortgang en
   * success-state zelf — met dezelfde formules als CartShippingMessage in theme.js.
   */
  function patchCartMessages(cart) {
    if (!igShippingThreshold || !cart) return;
    var messages = document.querySelectorAll('[data-cart-message]');
    if (!messages.length) return;

    var limitCents = thresholdCents(igShippingThreshold, false, 'shipping');
    var total = cart.total_price;
    var left = Math.max(0, limitCents - total);
    var leftText = window.themeCurrency && window.theme && window.theme.moneyFormat
      ? window.themeCurrency.formatMoney(left, window.theme.moneyFormat).replace('.00', '')
      : formatMoney(left, true);
    var percent = Math.min(100, total / limitCents * 100);
    var circumference = 28 * Math.PI;

    for (var i = 0; i < messages.length; i++) {
      var message = messages[i];
      message.setAttribute('data-limit', String(igShippingThreshold));
      message.classList.toggle('is-success',
        message.getAttribute('data-cart-message') === 'true' && total >= limitCents && total !== 0);

      var amounts = message.querySelectorAll('[data-left-to-spend]');
      for (var a = 0; a < amounts.length; a++) amounts[a].innerHTML = leftText;

      var bars = message.querySelectorAll('[data-cart-progress]');
      for (var b = 0; b < bars.length; b++) {
        if (bars[b].tagName.toLowerCase() === 'circle') {
          bars[b].style.strokeDashoffset = circumference - percent / 100 * circumference / 2;
        } else {
          bars[b].style.width = percent + '%';
        }
      }
    }
  }

  function fetchCart() {
    var root = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
    return fetch(root + '.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  /**
   * Intelligems klaar: testdrempel ophalen en alles wat de drempel toont bijwerken.
   * Draait één keer per pagina; zonder shipping-test gebeurt er niets.
   */
  var igApplied = false;
  function applyIgShipping() {
    if (igApplied) return;
    var value = readIgShippingThreshold();
    if (!value) return;
    igApplied = true;
    igShippingThreshold = value;

    upgradeLabels();
    document.dispatchEvent(new CustomEvent('bfe:market-threshold:change', {
      detail: { shippingThreshold: value, source: 'intelligems' }
    }));
    if (document.querySelector('[data-cart-message]')) {
      fetchCart().then(patchCartMessages).catch(function () {});
    }
  }

  window.BFEMarketThreshold.upgradeLabels = upgradeLabels;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      upgradeLabels();
    });
  } else {
    upgradeLabels();
  }

  /* Intelligems: kan al klaar zijn vóór dit (defer) script draait, dus ook direct checken */
  window.addEventListener('ig:ready', applyIgShipping);
  if (window.igData && window.igData.user) applyIgShipping();

  /* Cart drawer / section rendering vervangt DOM — opnieuw upgraden. */
  document.addEventListener('theme:cart:change', function (e) {
    upgradeLabels();
    /* Ná de Pipeline-handler, anders zet die de metaobject-drempel terug */
    var cart = e.detail && e.detail.cart;
    setTimeout(function () { patchCartMessages(cart); }, 0);
  });
  document.addEventListener('shopify:section:load', function (e) {
    upgradeLabels(e.target || document);
  });
})();
